import { countries, getEmojiFlag } from "countries-list"

export type CountryOption = {
  code: string   // ISO 3166-1 alpha-2, e.g. "BR"
  name: string   // English name, e.g. "Brazil"
  flag: string   // Emoji flag, e.g. "🇧🇷"
  label: string  // Composed display label, e.g. "🇧🇷 Brazil"
}

/**
 * Destination countries supported by `visa_metadata` lookups.
 * Must stay in sync with distinct `destination_code` values in the database.
 */
export const COUNTRY_OPTIONS: Array<{ code: string; label: string; flag: string }> = [
  { code: "AR", label: "Argentina", flag: "🇦🇷" },
  { code: "AU", label: "Australia", flag: "🇦🇺" },
  { code: "BR", label: "Brazil", flag: "🇧🇷" },
  { code: "CA", label: "Canada", flag: "🇨🇦" },
  { code: "CL", label: "Chile", flag: "🇨🇱" },
  { code: "CO", label: "Colombia", flag: "🇨🇴" },
  { code: "DE", label: "Germany", flag: "🇩🇪" },
  { code: "ES", label: "Spain", flag: "🇪🇸" },
  { code: "FR", label: "France", flag: "🇫🇷" },
  { code: "IE", label: "Ireland", flag: "🇮🇪" },
  { code: "IT", label: "Italy", flag: "🇮🇹" },
  { code: "MZ", label: "Mozambique", flag: "🇲🇿" },
  { code: "NZ", label: "New Zealand", flag: "🇳🇿" },
  { code: "PE", label: "Peru", flag: "🇵🇪" },
  { code: "PT", label: "Portugal", flag: "🇵🇹" },
  { code: "UK", label: "United Kingdom", flag: "🇬🇧" },
  { code: "US", label: "United States", flag: "🇺🇸" },
  { code: "SA", label: "Saudi Arabia", flag: "🇸🇦" },
] as const

/** Full sorted list of all countries with flag emoji. */
export const COUNTRY_LIST: CountryOption[] = Object.entries(countries)
  .map(([code, data]) => {
    const flag = getEmojiFlag(code as Parameters<typeof getEmojiFlag>[0])
    return {
      code,
      name: data.name,
      flag,
      label: `${flag} ${data.name}`,
    }
  })
  .sort((a, b) => a.name.localeCompare(b.name))

/** ISO code → country option */
const BY_CODE = new Map<string, CountryOption>(
  COUNTRY_LIST.map((c) => [c.code.toUpperCase(), c])
)

/** Country name (case-insensitive) → country option */
const BY_NAME = new Map<string, CountryOption>(
  COUNTRY_LIST.map((c) => [c.name.toLowerCase(), c])
)

export function getCountryByCode(code: string): CountryOption | undefined {
  return BY_CODE.get(code.toUpperCase())
}

/** Non-standard `visa_metadata.destination_code` values → ISO-3166 alpha-2 keys in `countries-list`. */
const STORED_DESTINATION_CODE_TO_ISO2: Record<string, string> = {
  UK: "GB",
}

/**
 * Resolve a DB `destination_code` to the ISO-2 key used by `countries-list` (e.g. `UK` → `GB`).
 */
export function iso3166Alpha2ForStoredDestinationCode(storedCode: string): string {
  const u = storedCode.trim().toUpperCase()
  return STORED_DESTINATION_CODE_TO_ISO2[u] ?? u
}

/** Label/flag for a stored `visa_metadata.destination_code` (handles `UK` vs `GB`). */
export function getCountryDisplayForStoredDestinationCode(storedCode: string): CountryOption | undefined {
  return getCountryByCode(iso3166Alpha2ForStoredDestinationCode(storedCode))
}

export function getCountryByName(name: string): CountryOption | undefined {
  return BY_NAME.get(name.toLowerCase())
}

/**
 * Returns the primary ISO currency code for a given country ISO code.
 * Uses `countries-list` data. Returns undefined for unknown inputs.
 */
export function getCountryCurrencyByCode(code: string): string | undefined {
  const iso = code.trim().toUpperCase()
  if (!iso || iso.length !== 2) return undefined
  const entry = (countries as unknown as Record<string, { currency?: unknown }>)[iso]
  const cur = entry?.currency
  if (typeof cur === "string" && cur.trim()) return cur.trim().toUpperCase()
  if (Array.isArray(cur) && cur.length > 0) {
    const first = cur[0]
    if (typeof first === "string" && first.trim()) return first.trim().toUpperCase()
  }
  return undefined
}

/**
 * Convert a 2-letter ISO code to its flag emoji.
 * Returns empty string for unknown codes.
 */
export function getCountryFlag(isoCode: string): string {
  return getCountryByCode(isoCode)?.flag ?? ""
}

/**
 * Extract a flag emoji from a workspace destination string.
 * The destination may be "Portugal · Lisboa" or "Portugal" or an ISO code "PT".
 * Returns empty string if no country can be matched.
 */
export function getFlagFromDestination(destination: string): string {
  if (!destination) return ""
  const raw = destination.split("·")[0]?.trim() ?? destination.trim()
  if (!raw) return ""
  // Try as ISO code first (2 chars)
  if (raw.length === 2) {
    const byCode = getCountryByCode(raw)
    if (byCode) return byCode.flag
  }
  // Try by name
  return getCountryByName(raw)?.flag ?? ""
}

/** Filter the list for a combobox search query. Returns up to `limit` results. */
export function searchCountries(query: string, limit = 10): CountryOption[] {
  const q = query.trim().toLowerCase()
  if (!q) return COUNTRY_LIST.slice(0, limit)
  return COUNTRY_LIST.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      c.code.toLowerCase().includes(q)
  ).slice(0, limit)
}
